import { Upload } from "lucide-react";
import { FranklinIcon } from "@/components/ui/franklin-icon";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { useLanguage } from "@/lib/i18n/LanguageContext";

interface AttachMenuItemsProps {
  /** Open the "Add from Franklin" document picker. */
  onAddFromFranklin: () => void;
  /** Trigger the file-upload flow (e.g. open the OS file dialog). */
  onUpload: () => void;
  /**
   * Set when uploading is not possible yet (no project chosen): the item is
   * disabled and says why, rather than silently doing nothing.
   */
  uploadDisabledReason?: string;
}

/**
 * AttachMenuItems — the two-item body ("Add from Franklin" / "Upload files")
 * shared by every DropdownMenu attach affordance on the issue page: the
 * expanded-view documents section (IssueAttachMenu) and the comment composer
 * (IssueComments). One place to keep the icons, labels, and ordering aligned.
 */
export function AttachMenuItems({
  onAddFromFranklin,
  onUpload,
  uploadDisabledReason,
}: AttachMenuItemsProps) {
  const { t } = useLanguage();
  return (
    <>
      <DropdownMenuItem onSelect={() => onAddFromFranklin()}>
        <FranklinIcon className="mr-2 h-3.5 w-3.5" />
        {t.pm.issues.detail.addFromFranklin}
      </DropdownMenuItem>
      <DropdownMenuItem
        disabled={uploadDisabledReason != null}
        onSelect={() => {
          // Defer past the menu's close so focus settles before the OS file
          // dialog opens.
          setTimeout(onUpload, 0);
        }}
      >
        <Upload className="mr-2 h-3.5 w-3.5" />
        <span className="flex min-w-0 flex-col">
          {t.pm.issues.detail.uploadFiles}
          {uploadDisabledReason && (
            <span className="text-[11px] text-muted-foreground">{uploadDisabledReason}</span>
          )}
        </span>
      </DropdownMenuItem>
    </>
  );
}
